import { styled } from '@linaria/react';
import { ActionIcon, Group, ScrollArea, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useAgentSession } from '../../../api/agent-miner-client/agent-miner-client.hooks';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import {
  baseBlack,
  baseWhite,
  inkDarkest,
  inkLight,
  inkLighter,
  secondaryBase,
  skyDark,
  skyLight,
  skyLightest,
} from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import { useShowAgentMinerApiCost } from '../shared/agent-miner.hooks';
import { AgentSourceIcon } from '../shared/agent-source-icon';
import { CategoryChip } from '../shared/category-chip';
import { fmtAbsolute, fmtCost, fmtNum } from '../shared/formatters';
import { getSessionDisplayTitle } from '../shared/shared.helpers';
import { StatusDot } from '../shared/status-dot';
import { TasksChipList } from '../shared/tasks-chip-list';
import { RelatedPrsCell } from '../sessions-list/cells/related-prs-cell';
import type { SessionDetailLocationState } from '../session-detail/session-detail.type';

type SessionPanelProps = {
  sessionId: string | null;
  onClose: () => void;
  /**
   * Renders a back arrow beside the title. Supplied when the panel was opened
   * from a session list (see `SessionListSideSection`) so the user can return
   * to that list instead of closing the whole side section.
   */
  onBack?: () => void;
  /**
   * In-app path the session detail page's Back button should return to.
   * Supplied by callers outside Agent Miner (e.g. the unified process graph);
   * omitted, Back goes to the Agent Miner sessions list.
   */
  returnTo?: string;
};

const dividerMargin = { margin: '16px 0' } as const;

type MetaItem = {
  iconName: string;
  label: string;
  value: string | React.ReactNode;
};

export function SessionPanel({ sessionId, onClose, onBack, returnTo }: SessionPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session, query } = useAgentSession(sessionId);
  const showApiCost = useShowAgentMinerApiCost();
  const goToDetails = () => {
    if (!session) return;
    const state: SessionDetailLocationState | undefined = returnTo ? { returnTo } : undefined;
    navigate(`/application/agent-miner/sessions/${session.id}`, { state });
  };

  if (!session) {
    return (
      <Wrapper>
        <BrandedLoadingOverlay visible={query.isLoading} variant="colored" />
        {/* Keep the back affordance during the detail fetch — without it a slow
            or failed request strands the user with no route back to the list. */}
        {onBack && (
          <ActionIcon
            variant="subtle"
            size="sm"
            radius="xl"
            color={skyDark}
            onClick={onBack}
            aria-label={t('common.actions.back')}
          >
            <Icon name="arrow_back" size={18} color={skyDark} />
          </ActionIcon>
        )}
      </Wrapper>
    );
  }

  const inTokens = session.total_input_tokens ?? 0;
  const outTokens = session.total_output_tokens ?? 0;
  const cacheTokens = (session.total_cache_creation_tokens ?? 0) + (session.total_cache_read_tokens ?? 0);
  const turnsCount = session.turns_count ?? session.turns?.length ?? 0;
  // The retrieve endpoint doesn't annotate `events_count` (only the list
  // endpoint does). Fall back to summing events across the nested turns.
  const eventsCount =
    session.events_count ?? session.turns?.reduce((sum, turn) => sum + (turn.events?.length ?? 0), 0) ?? 0;

  const displayName = getSessionDisplayTitle(session);

  const categoryLabel = session.flora_annotations?.label ?? null;

  const metadata: MetaItem[] = [];
  if (session.user?.name) metadata.push({ iconName: 'person', label: 'User', value: session.user.name });
  metadata.push({ iconName: 'schedule', label: 'Started', value: fmtAbsolute(session.started_at) });
  metadata.push({
    iconName: 'history',
    label: t('agent_miner.last_activity'),
    value: fmtAbsolute(session.last_turn_at ?? session.started_at),
  });
  if (session.duration_ms != null) {
    const minutes = Math.max(0, Math.round(session.duration_ms / 60000));
    metadata.push({ iconName: 'timer', label: 'Duration', value: `${minutes} min` });
  }
  if (session.working_directory) {
    const trimmed = session.working_directory.replace(/\/+$/, '');
    const leaf = trimmed.split('/').filter(Boolean).pop() ?? trimmed;
    metadata.push({ iconName: 'folder_open', label: 'Directory', value: leaf });
  }
  if (session.linked_tasks && session.linked_tasks.length > 0) {
    metadata.push({
      iconName: 'link',
      label: t('common.work_units'),
      value: <TasksChipList tasks={session.linked_tasks} size="sm" wrap />,
    });
  }
  if ((session.related_prs?.count ?? 0) > 0) {
    metadata.push({
      iconName: 'call_merge',
      label: t('agent_miner.related_prs'),
      value: <RelatedPrsCell session={session} wrap />,
    });
  }
  if (categoryLabel) {
    metadata.push({
      iconName: 'sell',
      label: t('agent_miner.category'),
      value: <CategoryChip label={categoryLabel} size="sm" />,
    });
  }

  return (
    <Wrapper>
      <BrandedLoadingOverlay visible={query.isLoading} variant="colored" />
      {/* Scrollbar hidden; the panel still scrolls by wheel, trackpad and keyboard. */}
      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="never">
        <Body>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap={8} align="center" wrap="nowrap" style={{ minWidth: 0 }}>
              {onBack && (
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  radius="xl"
                  color={skyDark}
                  onClick={onBack}
                  aria-label={t('common.actions.back')}
                >
                  <Icon name="arrow_back" size={18} color={skyDark} />
                </ActionIcon>
              )}
              <AgentSourceIcon source={session.source} size={20} />
              <Text weight="bold" size="medium" color={secondaryBase}>
                {displayName}
              </Text>
            </Group>
            <ActionIcon
              variant="subtle"
              size="sm"
              radius="xl"
              color={skyDark}
              onClick={onClose}
              aria-label={t('common.actions.close')}
            >
              <Icon name="close" size={18} color={skyDark} />
            </ActionIcon>
          </Group>

          <Group gap={6} mt={6} align="center">
            <StatusDot status={session.status} />
            <Text size="small" weight="medium" color={inkLight}>
              {session.status}
            </Text>
          </Group>

          <Divider style={dividerMargin} />

          <Stack gap={6}>
            {metadata.map((item) => (
              <Group key={item.label} justify="space-between" wrap="nowrap" gap={8}>
                <MetaLabel>
                  <Icon name={item.iconName} size={14} color={inkLighter} />
                  <Text size="small" color={inkLighter}>
                    {item.label}
                  </Text>
                </MetaLabel>
                {typeof item.value === 'string' ? (
                  <MetaValue>
                    <Text size="small" color={baseBlack}>
                      {item.value}
                    </Text>
                  </MetaValue>
                ) : (
                  item.value
                )}
              </Group>
            ))}
          </Stack>

          <Divider style={dividerMargin} />

          <StatTileGrid>
            <StatTile>
              <StatLabel>
                <Icon name="forum" size={12} color={inkLight} />
                Turns
              </StatLabel>
              <StatValue>{turnsCount}</StatValue>
            </StatTile>
            <StatTile>
              <StatLabel>
                <Icon name="bolt" size={12} color={inkLight} />
                Events
              </StatLabel>
              <StatValue>{eventsCount}</StatValue>
            </StatTile>
            <StatTile>
              <StatLabel>
                <Icon name="memory" size={12} color={inkLight} />
                Tokens
              </StatLabel>
              <StatValue>{fmtNum(inTokens + outTokens)}</StatValue>
              {cacheTokens > 0 && <StatSub>+{fmtNum(cacheTokens)} cache</StatSub>}
            </StatTile>
            {showApiCost && (
              <StatTile>
                <StatLabel>
                  <Icon name="payments" size={12} color={inkLight} />
                  API cost
                </StatLabel>
                <StatValue>{fmtCost(session.total_cost_usd)}</StatValue>
              </StatTile>
            )}
          </StatTileGrid>

          <Divider style={dividerMargin} />

          <Button
            fullWidth
            size="sm"
            radius="xl"
            variant="secondary"
            onClick={goToDetails}
            leftSection={<Icon name="open_in_full" variant="filled" size={14} color={baseWhite} />}
          >
            Open session details
          </Button>
        </Body>
      </ScrollArea>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  position: relative;
`;

const Body = styled.div`
  padding: 4px 0 16px;
  display: flex;
  flex-direction: column;
`;

const MetaLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const MetaValue = styled.span`
  max-width: 60%;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatTileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const StatTile = styled.div`
  background: ${skyLightest};
  border: 1px solid ${skyLight};
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: 600 10px/1 'Figtree', sans-serif;
  color: ${inkLight};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const StatValue = styled.div`
  font: 600 16px/1.1 'Figtree', sans-serif;
  color: ${inkDarkest};
  font-variant-numeric: tabular-nums;
`;

const StatSub = styled.div`
  font: 400 10.5px/1 'Figtree', sans-serif;
  color: ${inkLighter};
`;
